<template>
  <v-app>
    <v-main>
      <ParticleBackground />

      <!-- ALERT -->
      <transition name="fade">
        <v-alert
          v-if="showAlert"
          type="error"
          class="position-fixed top-0 left-0 right-0 mx-auto mt-2"
          style="max-width: 400px; z-index: 30"
          border="start"
          elevation="10"
          closable
          @click:close="showAlert = false"
        >
          {{ alertMessage }}
        </v-alert>
      </transition>

      <div class="login-background d-flex align-center justify-start">
        <!-- Idioma + tema: arriba a la derecha de la pantalla -->
        <div class="login-theme-toggle">
          <LanguajeSelector />
          <TemaApp />
        </div>
        <div
          :class="['glass-bg', { 'glass-bglight': !isDark }]"
          aria-hidden="true"
        >
          <span class="blob b1"></span>
          <span class="blob b2"></span>
          <span class="blob b3"></span>
          <span class="grain"></span>
        </div>

        <v-col cols="12" sm="8" md="4" class="login-layer">
          <v-card :class="isDark ? 'card-login-reves':'card-login'" elevation="12">

            <v-card-text class="cool-card login-card">
              <!-- Logo -->
              <img
                :src="require('@/assets/prometheus-light.png')"
                alt="Prometheus"
                class="login-logo"
              />

              <h1 class="login-heading">{{ $t("login_title") }}</h1>

              <form ref="form" @submit.prevent="loginHandler" novalidate>
                <label class="field-label">{{ $t("username") }}</label>
                <v-text-field
                  v-model="username"
                  type="text"
                  :placeholder="$t('username')"
                  variant="solo"
                  flat
                  rounded="lg"
                  hide-details="auto"
                  :error-messages="errors.username.map((e) => $t(e))"
                  @input="errors.username = []"
                ></v-text-field>

                <label class="field-label">{{ $t("password") }}</label>
                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('password')"
                  variant="solo"
                  flat
                  rounded="lg"
                  hide-details="auto"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPassword = !showPassword"
                  :error-messages="errors.password.map((e) => $t(e))"
                  @input="errors.password = []"
                ></v-text-field>

                <div class="text-right mt-1">
                  <a
                    class="login-link"
                    href="#"
                    @click.prevent="openForgotPasswordModal"
                  >
                    {{ $t("forgot_password") }}
                  </a>
                </div>

                <v-btn
                  type="submit"
                  class="mt-5 login-submit"
                  color="primary"
                  block
                  size="large"
                >
                  {{ $t("login") }}
                </v-btn>
              </form>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Titulo de bienvenida a la derecha -->
        <div
          class="login-hero"
          :style="{ color: isDark ? '#ffffff' : '#012B59' }"
        >
          <h1 class="hero-title">{{$t("bienvenidoLogin")}}</h1>
          <p class="hero-subtitle">{{$t("bienvenidoLogin1")}}</p>
        </div>

        <!-- MODAL RESET -->
        <ResetPass v-if="showModal" @close="closeModal" />

        <!-- MFA (2FA): setup con QR o verificacion -->
        <MfaDialog
          v-model="mfaDialog"
          :mode="mfaMode"
          :mfa-token="mfaToken"
          @verified="onMfaVerified"
        />
        <v-snackbar
          v-model="showSnackbar"
          :color="snackbarColor"
          timeout="4000"
          location="top"
          style="z-index: 35"
        >
          <v-icon start>{{ icono }}</v-icon>
          {{ snackbarMessage }}
        </v-snackbar>
      </div>

      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import "@/css/Login/StylesLogin.css";
import api from "@/api/axios/axios.js";
import ResetPass from "@/components/Global/ResetPass.vue";
import ParticleBackground from "@/components/Global/ParticleBackground.vue";
import TemaApp from "@/components/Global/TemaApp.vue";
import LanguajeSelector from "@/components/Global/LanguajeSelector.vue";
import MfaDialog from "@/components/Global/MfaDialog.vue";
import { useSettingsStore } from "@/stores/settings";
import { useAuthStore } from "@/stores/auth";
import { swal } from "@/utils/swal";
export default {
  name: "LoginPage",

  components: {
    ResetPass,
    TemaApp,
    ParticleBackground,
    LanguajeSelector,
    MfaDialog,
  },

  data() {
    return {
      username: "",
      password: "",
      showAlert: false,
      alertMessage: "",
      showPassword: false,
      snackbarColor: "warning",
      showSnackbar: false,
      snackbarMessage: "",
      icono: null,
      showModal: false,
      // MFA
      mfaDialog: false,
      mfaMode: "verify", // "setup" | "verify"
      mfaToken: "",
      errors: {
        username: [],
        password: [],
      },
    };
  },

  mounted() {
    const errmensaje = this.$route.query.error;
    const succesmensaje = this.$route.query.mensaje;
    const navType = performance.getEntriesByType("navigation")[0]?.type;

    if (errmensaje || succesmensaje) {
      if (this.$route.query.error === "session_expired") {
        this.snackbarMessage = this.$t("session_expired");
        this.snackbarColor = "error";
        this.icono = "mdi-alert-circle";
      } else {
        this.snackbarMessage = succesmensaje;
        this.snackbarColor = "success";
        this.icono = "mdi-check-circle-outline";
      }

      this.showSnackbar = true;

      if (navType === "reload") {
        this.$router.replace({ path: this.$route.path, query: {} });
      }
    }
  },
  computed: {
    isDark() {
      const settings = useSettingsStore();
      return settings.theme === "dark";
    },
  },

  methods: {
    async loginHandler() {
      this.errors = { username: [], password: [] };
      if (!this.username) {
        this.errors.username.push("completa");
      }
      if (!this.password) {
        this.errors.password.push("completa");
      }
      if (this.errors.username.length || this.errors.password.length) return;
      try {
        //console.log("Envio de Credenciales al backend");
        const response = await api.post(
          "login/",
          {
            username: this.username,
            password: this.password,
          },
          {
            meta: { triggeredByButton: true }, //clave personalizada
          },
        );
        const d = response.data || {};

        // MFA: primera vez (enrolar) o ya enrolado (verificar).
        if (d.mfa_setup_required) {
          this.mfaToken = d.mfa_token;
          this.mfaMode = "setup";
          this.mfaDialog = true;
          return;
        }
        if (d.mfa_required) {
          this.mfaToken = d.mfa_token;
          this.mfaMode = "verify";
          this.mfaDialog = true;
          return;
        }

        // Sin MFA: login directo.
        await this.entrarAlDashboard(d.exp);
      } catch (err) {
        this.alertMessage =
          err.response?.data?.error || this.$t("login_failed"); // encadenamiento opcional
        this.showAlert = true;
        this.timeOutAlert = setTimeout(() => {
          this.showAlert = false;
        }, 5000);
      }
    },
    // Pasos comunes tras un login valido (con o sin MFA): rol, sesion y entrada.
    async entrarAlDashboard(exp) {
      if (exp) {
        localStorage.setItem("token_exp", exp);
      }
      // Reseteamos y cargamos el rol del NUEVO usuario en el store.
      const auth = useAuthStore();
      auth.clear();
      const dashlogin = await auth.fetchRole();
      localStorage.setItem("role", dashlogin.role);
      // Sesion unica por navegador.
      const sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("my_session", sid);
      localStorage.setItem("active_session", sid);
      this.$router.push("/dashboard");
      await swal({
        icon: "success",
        title: this.$t("welcome"),
        text: this.$t("login_success_user", { username: this.username }),
        timer: 2500,
        showConfirmButton: false,
        customClass: {
          popup: this.isDark ? "mi-popup" : "mi-popup-2",
          title: "mi-titulo",
        },
      });
    },
    // El dialogo MFA enrolo/verifico con exito -> entramos.
    async onMfaVerified(data) {
      this.mfaDialog = false;
      try {
        await this.entrarAlDashboard(data?.exp);
      } catch (e) {
        this.alertMessage = this.$t("login_failed");
        this.showAlert = true;
      }
    },
    openForgotPasswordModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>
